import { DocumentTypeLabelPipe } from '../../../pipes/document-type-label.pipe';
import { getDocumentTypeLabel } from '../../../helpers/document-type-label.helper';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DriverDocumentsService } from '../../../services/driver-documents.service';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DocumentType, UploadedDocuments, UploadProgressStatus } from '../../../models/driver-documents.model';
import { FlatpickrDatePickerComponent } from '../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-upload-driver-documents-modal',
  standalone: true,
  imports: [DocumentTypeLabelPipe, CommonModule, FormsModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './upload-driver-documents-modal.component.html',
  styleUrl: './upload-driver-documents-modal.component.scss'
})
export class UploadDriverDocumentsModalComponent {

  driverId: number = 0;
  file: File | null = null;
  selectedDocumentType: string = '';
  documentTypes: DocumentType[] = [];
  driversLicenseRequired: boolean = false;
  uploadedDocumentsDetails: { names: string[], hasLicensePdp: boolean } = { names: [], hasLicensePdp: false };
  uploadProgress: number = 0;
  uploading: boolean = false;
  uploadSuccess: boolean = false;
  dropdownOpen: boolean = false;
  uploadedDocuments: UploadProgressStatus[] = [];
  otherDocumentName: string = '';
  isLoading: boolean = false;
  documentRenewalDate: string = '';
  licensePdpRenewalDate: string = '';

  private modalInstance = inject(NgbActiveModal);
  private transloco = inject(TranslocoService);
  private driverDocumentsService = inject(DriverDocumentsService);
  private confirmationModalService = inject(ConfirmationModalService);
  private errorHandlerService = inject(ErrorHandlerService);

  private _hasLicensePdp = false;
  private _otherDocumentExpires = false;

  get availableDocumentTypes() {
    const takenBefore = new Set(this.uploadedDocumentsDetails.names);
    const takenCurrent = new Set([...takenBefore, ...this.uploadedDocuments.map(uploadedDocument => uploadedDocument.documentName)]);
    const hasDriversLicenseWithPdp = this.uploadedDocumentsDetails.hasLicensePdp || this.uploadedDocuments.some(
      doc => doc.documentName === 'Drivers License' && doc.hasLicensePdp
    );

    return this.documentTypes.filter(t => {
      if (t.name === 'Other') {
        return true;
      }

      if (hasDriversLicenseWithPdp && t.name === 'Professional Driving Permit') {
        return false;
      }

      return !takenCurrent.has(t.name);
    });
  }

  get documentTypeName(): string {
    return this.documentTypes.find(type => type.name === this.selectedDocumentType)?.name || '';
  }

  get documentTypeExpires(): boolean {
    return this.documentTypes.find(type => type.name === this.selectedDocumentType)?.validUntilRequired || false;
  }

  get isOtherDocumentNameValid(): boolean {
    return this.selectedDocumentType !== 'Other' || this.otherDocumentName.trim().length > 0;
  }

  get canUploadSelectedDocument(): boolean {
    return !!this.selectedDocumentType &&
      this.isOtherDocumentNameValid &&
      (!this.documentTypeExpires || !!this.documentRenewalDate) &&
      !(this.selectedDocumentType === 'Drivers License' && this.hasLicensePdp && !this.licensePdpRenewalDate);
  }

  get hasLicensePdp(): boolean {
    return this._hasLicensePdp;
  }

  set hasLicensePdp(value: boolean) {
    this._hasLicensePdp = value;
  }

  get otherDocumentExpires(): boolean {
    return this._otherDocumentExpires;
  }

  set otherDocumentExpires(value: boolean) {
    this._otherDocumentExpires = value;
  }

  get isProfessionalDrivingPermitUploaded(): boolean {
    return this.uploadedDocumentsDetails.names.includes('Professional Driving Permit') ||
      this.uploadedDocuments.some(doc => doc.documentName === 'Professional Driving Permit');
  }

  get isDriversLicenseUploaded(): boolean {
    return this.uploadedDocumentsDetails.names.includes('Drivers License') ||
      this.uploadedDocuments.some(doc => doc.documentName === 'Drivers License');
  }

  get showDriversLicenseWarning(): boolean {
    return this.driversLicenseRequired && !this.isDriversLicenseUploaded;
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectDocument(document: any) {
    this.selectedDocumentType = document.name;
    this.dropdownOpen = false;
    this.otherDocumentName = '';
    this.otherDocumentExpires = false;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!this.canUploadSelectedDocument) {
      input.value = '';
      return;
    }

    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];

      const maxSizeInBytes = 5 * 1024 * 1024;
      if (selectedFile.size > maxSizeInBytes) {
        alert('File size must be less than or equal to 5MB.');
        input.value = '';
        return;
      }

      this.file = selectedFile;
      this.uploadTempDocument();
    }
  }

  async uploadTempDocument() {
    try {
      if (!this.file || !this.canUploadSelectedDocument) return;

      const item: UploadProgressStatus = {
        reference: crypto.randomUUID(),
        driverId: this.driverId,
        documentName: this.selectedDocumentType === 'Other' ? this.toTitleCase(this.otherDocumentName.trim()) : this.selectedDocumentType,
        extension: this.file?.name ? `.${this.file.name.split('.').pop()}` : '',
        documentTypeId: this.documentTypes.find(type => type.name === this.selectedDocumentType)?.id || 0,
        hasLicensePdp: this.hasLicensePdp,
        document: this.file,
        uploading: true,
        uploadProgress: 0,
        uploadSuccess: false,
        documentRenewalDate: this.documentRenewalDate || null,
        pdpRenewalDate: this.hasLicensePdp ? this.licensePdpRenewalDate : null
      }

      this.uploadedDocuments.push(item);
      this.resetState();

      const tempDocFormData = this.getTempDocumentToUpload(item);
      this.driverDocumentsService.uploadTempDocuments(tempDocFormData).then(() => {
        item.uploadProgress = 100;
        setTimeout(() => {
          item.uploading = false;
          item.uploadSuccess = true;

          clearInterval(interval);
        }, 500);
      });

      const interval = setInterval(() => {
        if (item.uploadProgress < 90) {
          item.uploadProgress += 1;
        }
      }, 100);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  allUploadsSuccessful(): boolean {
    return this.uploadedDocuments.length > 0 &&
      this.uploadedDocuments.every(doc => doc.uploadSuccess === true);
  }

  removeUploadedDocument(doc: any) {
    this.uploadedDocuments = this.uploadedDocuments.filter(d => d !== doc);
  }

  async saveChanges() {
    this.isLoading = true;

    try {
      const uploadedDocuments = this.getUploadedDocuments();
      await this.driverDocumentsService.uploadDriverDocument(uploadedDocuments);

      this.modalInstance.close();
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  closeModal() {
    this.modalInstance.dismiss();
  }

  getDropdownHeight(): string {
    if (this.selectedDocumentType === 'Other' && this.otherDocumentExpires) {
      return '350px';
    }

    if (this.selectedDocumentType === 'Drivers License') {
      return '300px';
    }

    if (!this.selectedDocumentType || (this.selectedDocumentType && !this.documentTypeExpires)) {
      return '150px';
    }

    return '250px';
  }

  openDeleteDriverDocumentConfirmationModal(document: any) {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('driverDocuments.deletePrompt.title'),
      message: this.transloco.translate('driverDocuments.deletePrompt.message', { name: getDocumentTypeLabel(this.transloco, document.documentName) }),
      confirmText: this.transloco.translate('driverDocuments.deletePrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline')
    }

    this.confirmationModalService.showConfirmationModal(options).then(async () => {
      this.removeUploadedDocument(document);
    });
  }

  private resetState() {
    this.file = null;
    this.selectedDocumentType = '';
    this.otherDocumentName = '';
    this.otherDocumentExpires = false;
    this.documentRenewalDate = '';
    this.licensePdpRenewalDate = '';
    this.hasLicensePdp = false;

    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  private getTempDocumentToUpload(doc: UploadProgressStatus): FormData {
    const formData = new FormData();
    formData.append('DriverId', doc.driverId.toString());
    formData.append('DocumentName', doc.documentName);
    formData.append('Reference', doc.reference);
    formData.append('Document', doc.document);

    return formData;
  }

  private getUploadedDocuments(): UploadedDocuments[] {
    const documents: UploadedDocuments[] = [];
    this.uploadedDocuments.forEach(doc => {
      documents.push({
        driverId: doc.driverId,
        documentName: doc.documentName,
        extension: doc.extension,
        documentTypeId: doc.documentTypeId,
        reference: doc.reference,
        documentRenewalDate: doc.documentRenewalDate,
        pdpRenewalDate: doc.pdpRenewalDate,
        hasLicensePdp: doc.hasLicensePdp
      });
    });

    return documents;
  }

  private toTitleCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}